import React, { useContext, useEffect, useMemo, useState } from "react";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import CommonModal from "../../../Modals/CommonModal";
import apiGETRequest from "../../../../utils/apiRequest";
import { csrfToken } from "../../../../utils/csrfTokenUtils";
import Button from "../../../Elements/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faCircle} from "@fortawesome/free-solid-svg-icons";
import { networkService } from "../../../config/axios";
import { UserTypes } from "../../../../hooks/utils";
import UserInfoContext from "../../../../utils/userInfoContext";
import AdminPagination from "../../../CommonComponents/Pagination/AdminPagination";
import DeleteOrgModal from "../../../Modals/Admin/DeleteOrgModal";
import MoveDataModal from "../../../Modals/Admin/MoveDataModal";
import CompanyUsersModal from "../../../Modals/Admin/CompanyUsersModal";
import UserDomainsModal from "../../../Modals/Admin/UserDomainsModal";
import BulkUserDomainsModal from "../../../Modals/Admin/BulkUserDomainsModal";
import CustomTooltip from "../../../Elements/Tooltip";
import { ImpersonationContext } from "../../../Impersonation/ImpersonationProvider";

function buildQuery(obj, prefix) {
    const queryString = Object.keys(obj).map(key => {
        const value = obj[key];
        const fullKey = prefix ? `${prefix}[${key}]` : key;
        if (value !== null && typeof value === 'object') {
            return buildQuery(value, fullKey);
        } else {
            return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value)}`;
        }
    });
    return queryString.join('&');
}

const fmtDate = (d) => {
    if (!d || d === '-') return d;
    const [date, time] = d.split(' @ ');
    return time ? <>{date} @<br />{time}</> : d;
};

const UsersListing = ({ showCheckbox = true, setData, companyId, type, dataPerPage = 10, selectAction, setSelectAction, onTotalDataChange, searchTerm = '' }) => {
    const { setShowNotifications, userInfoData } = useContext(UserInfoContext)
    const { canRequest, requestImpersonation } = useContext(ImpersonationContext);
    const canImpersonateRow = (user) =>
        canRequest && (userInfoData?.user_type == 1 || Number(user?.user_type ?? user?.type) > 2);
    const [users, setUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [refetch, setRefetch] = useState(true);
    const [deleteModal, setDeleteModal] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [currentPageData, setCurrentPageData] = useState([]);
    const [userIds, setUserIds] = useState([]);
    const [sortState, setSortState] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [openUserDomains, setOpenUserDomains] = useState(false);
    const [openUserBulkDomains, setOpenUserBulkDomains] = useState(false);
    useEffect(() => {
        if (
            selectAction === "bulkDomain" &&
            userIds.length > 0 &&
            !openUserBulkDomains
        ) {
            setOpenUserBulkDomains(true);
        }
    }, [selectAction]);

    const handleCloseModal = () => {
        setSelectAction('action')
    }

    const adduserIds = (id) => {
        setUserIds((prevUserIds) => {
            if (prevUserIds.includes(id)) {
                return prevUserIds.filter((userId) => userId !== id);
            } else {
                return [...prevUserIds, id];
            }
        });
    }

    const handlePerformAction = () => {
        const data = {
            user_ids: userIds
        }
        const url = selectAction == 'sendMail' ? `admin/users/send_confirmation_email.json` : `admin/users/destroy_all`;
        networkService(url, selectAction == 'sendMail' ? "POST" : "DELETE", data)
            .then((response) => {
                if (response.status == 204) {
                    handleCloseModal()
                    setShowNotifications({
                        showToast: true,
                        toastType: "success",
                        toastMessage: `${selectAction == 'sendMail' ? "Mail sent to all users" : 'Deleted all users'}`,
                    })
                    setRefetch(true);
                }
            })
            .catch((error) => {
                handleCloseModal()
                setShowNotifications({
                    showToast: true,
                    toastType: 'danger',
                    toastMessage: "Something went wrong"
                })
                setUserIds([])
            });
    }

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        if (type !== 'userManagement' && type !== 'bannedUser') {
            getUsers(companyId, pageNumber);
        }
    };

    const getUsers = async (companyId, page = 1) => {
        const params = {
            start: (page - 1) * dataPerPage,
            length: dataPerPage,
            draw: 1,
            search: { value: searchTerm.length >= 3 ? searchTerm : '', regex: false },
            _: Date.now()
        };
        if (type == 'userManagement' && companyId) {
            const userLst = await networkService(`admin/company-users/${companyId}.json`, 'GET')
            delete userLst?.data?.users_length;
            setUsers([...Object.values(userLst?.data)])
        } else if (type == 'bannedUser' && companyId) {
            const userList = await networkService(`admin/banned_users/${companyId}.json`, 'GET')
            setUsers(userList?.data?.users)
        }
        else {
            const queryString = buildQuery(params);
            const response = await apiGETRequest(`admin/users_listing?${queryString}`);
            setUsers([...response?.data]);
            setTotalCount(response?.recordsFiltered ?? response?.recordsTotal ?? 0);
        }
    }

    const handleDelete = async () => {
        try {
            const response = await networkService(`admin/users/${selectedUser.id}.json?banned=true`, 'DELETE');
            if (response.status == 204) {
                setSelectedUser(null);
                setDeleteModal(null);
                setRefetch(true);
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "user has been banned",
                })
            }
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage: "Something went wrong"
            })
        }
    }

    const handlePermanentDelete = async () => {
        try {
            const response = await networkService(`admin/users/${selectedUser.id}.json`, 'DELETE');
            if (response.status == 204) {
                setSelectedUser(null);
                setDeleteModal(null);
                setRefetch(true);
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "User has been permanently deleted",
                });
            }
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage: "Something went wrong"
            });
        }
    };

    const handleMoveModalDeleteAll = async () => {
        try {
            const response = await networkService(`admin/users/${selectedUser.id}.json`, 'DELETE');
            if (response.status == 204) {
                setSelectedUser(null);
                setDeleteModal(null);
                setRefetch(true);
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "User and all data have been permanently deleted",
                });
            }
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage: "Something went wrong"
            });
        }
    };

    const handlePermit = async (user) => {
        const data = {
            authenticity_token: csrfToken(),
            id: user?.id,
            user: {
                company_id: user?.company_id,
                email: user?.email,
                user_type: user?.user_type,
                banned: false,
            }
        }
        try {
            const response = await networkService(`admin/users/${user?.id}.json`, 'PATCH', data);
            if (response.status == 200) {
                setRefetch(true);
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "User account is now active again!",
                })
            }
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage: "Something went wrong"
            })
        }
    }

    const handleSortChange = (sort) => {
        if (
            (sortState && sort && sortState.id === sort.id && sortState.desc === sort.desc) ||
            (!sortState && !sort)
        ) {
            return;
        }
        setSortState(sort || null);
    };

    useEffect(() => {
        if (refetch) {
            getUsers(companyId);
            setRefetch(false);
        }
    }, [refetch, companyId]);

    useEffect(() => {
        if (type !== 'userManagement' && type !== 'bannedUser') {
            if (true) {
                setCurrentPage(1);
                getUsers(companyId, 1);
            }
        }
    }, [searchTerm]);

    useEffect(() => {
        if (type === 'userManagement' || type === 'bannedUser') {
            setCurrentPage(1);
        }
    }, [users.length, dataPerPage]);

    useEffect(() => {
        if (sortState) {
            setCurrentPage(1);
        }
    }, [sortState]);

    const normalizeSortValue = (value) => {
        if (value === undefined || value === null) return '';
        if (value instanceof Date) return value.getTime();
        if (typeof value === 'number') return value;
        if (typeof value === 'boolean') return value ? 1 : 0;
        if (typeof value === 'string') {
            const timestamp = Date.parse(value);
            if (!Number.isNaN(timestamp) && /\d/.test(value)) {
                return timestamp;
            }
            return value.toLowerCase();
        }
        return value;
    };

    const getSortValue = (row, id) => {
        switch (id) {
            case 'type_name':
                return type === 'bannedUser'
                    ? UserTypes[row?.user_type ?? row?.current_user_type] ?? ''
                    : row?.type_name ?? '';
            case 'country':
                if (typeof row?.country === 'object' && row?.country) {
                    return row?.country?.names?.en || row?.country?.iso_code || '';
                }
                return row?.country || row?.country_iso || '';
            case 'pp_acceptance_on':
            case 'tos_acceptance_on':
            case 'last_login':
                return row?.[id] ?? '';
            default:
                return row?.[id];
        }
    };

    const sortedUsers = useMemo(() => {
        if (!sortState || !sortState.id || ['selection', 'action'].includes(sortState.id)) {
            return [...users];
        }
        const sorted = [...users].sort((a, b) => {
            const aValue = normalizeSortValue(getSortValue(a, sortState.id));
            const bValue = normalizeSortValue(getSortValue(b, sortState.id));

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return aValue - bValue;
            }
            return String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
        });
        return sortState.desc ? sorted.reverse() : sorted;
    }, [users, sortState, type]);

    const isServerPaginated = type !== 'userManagement' && type !== 'bannedUser';
    const totalData = isServerPaginated ? totalCount : sortedUsers.length;

    // Notify parent of total data changes
    useEffect(() => {
        if (onTotalDataChange) {
            onTotalDataChange(totalData);
        }
    }, [totalData, onTotalDataChange]);

    useEffect(() => {
        if (!isServerPaginated) {
            const totalPages = Math.max(1, Math.ceil(sortedUsers.length / dataPerPage));
            setCurrentPage((prev) => Math.min(prev, totalPages));
        }
    }, [sortedUsers.length, dataPerPage]);

    useEffect(() => {
        if (isServerPaginated) {
            setCurrentPageData([...sortedUsers]);
        } else {
            const indexOfLastData = currentPage * dataPerPage;
            const indexOfFirstData = indexOfLastData - dataPerPage;
            setCurrentPageData(sortedUsers.slice(indexOfFirstData, indexOfLastData));
        }
    }, [sortedUsers, currentPage, dataPerPage]);

    useEffect(() => {
        if (!isServerPaginated) {
            setCurrentPage(1);
        }
    }, [users.length, dataPerPage, sortState]);

    // Refetch data when dataPerPage changes for server-paginated views
    useEffect(() => {
        if (isServerPaginated && !refetch) {
            setCurrentPage(1);
            getUsers(companyId, 1);
        }
    }, [dataPerPage]);

    const columns = useMemo(() => {
        return [
            ...(showCheckbox ? [{
                Header: '',
                id: "selection",
                disableSortBy: true,
                Cell: ({ row: { original } }) => (
                    <div className="text-center">
                        <input
                            className="cursor-pointer"
                            type="checkbox"
                            checked={userIds.includes(original?.id)}
                            onChange={() => adduserIds(original?.id)}
                        />
                    </div>
                ),
            }] : []),
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Email',
                accessor: 'email',
            },
            // banned_users endpoint doesn't return these counts
            ...(type !== 'bannedUser' ? [{
                Header: () => (
                    <CustomTooltip title="Past 30 days">
                        <span>Visits</span>
                    </CustomTooltip>
                ),
                id: 'visits_count',
                accessor: 'visits_count',
                Cell: ({ row }) => <>{row?.original?.visits_count ?? 0}</>,
            }, {
                Header: () => (
                    <CustomTooltip title="LaserLINX this user created">
                        <span>LLINX</span>
                    </CustomTooltip>
                ),
                id: 'tracking_links_count',
                accessor: 'tracking_links_count',
                Cell: ({ row }) => <>{row?.original?.tracking_links_count ?? 0}</>,
            }] : []),
            ...(!showCheckbox ? [{
                Header: 'User Type',
                accessor: 'type_name',
                Cell: ({ row: { original } }) => (<>{type == 'bannedUser' ? UserTypes[original?.user_type ?? original?.current_user_type] : original?.type_name}</>)
            }] : []),
            {
                Header: 'Last Login',
                accessor: 'last_login',
                Cell: ({ row }) => <>{fmtDate(row?.original?.last_login) || '-'}</>,
            },
            {
                Header: 'Country',
                accessor: 'country',
                Cell: ({ row: { original } }) => {
                    const countryIso = original?.country_iso || (typeof original?.country === 'object' && original?.country?.iso_code);
                    const countryName = typeof original?.country === 'string' 
                        ? original?.country 
                        : (original?.country?.names?.en || original?.country?.iso_code);
                    
                    return (
                        <>
                            {countryIso ? (
                                <div
                                    style={{ width: '22px' }}
                                    className={`flag-icon flag-icon-${countryIso.toLowerCase()}`}
                                    title={countryName || countryIso}
                                ></div>
                            ) : (
                                <>{countryName || '-'}</>
                            )}
                        </>
                    );
                }
            },
            ...(type !== 'bannedUser' ? [{
                Header: 'PP Acceptance on',
                accessor: 'pp_acceptance_on',
                Cell: ({ row }) => fmtDate(row?.original?.pp_acceptance_on) || '-',
            }] : []),
            ...(type !== 'bannedUser' ? [{
                Header: 'ToS Acceptance on',
                accessor: 'tos_acceptance_on',
                Cell: ({ row }) => fmtDate(row?.original?.tos_acceptance_on) || '-',
            }] : []),
            {
                Header: 'Action',
                accessor: 'action',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <>
                        {type == 'bannedUser' ?(
                            <div className="d-flex align-items-center gap-1">
                                <Button
                                    text={'Reactivate'}
                                    classList={'success'}
                                    onClick={() => handlePermit(row?.original)}
                                />
                                <CustomTooltip title="Delete User Permanently">
                                    <img
                                        src="/icons/delete.svg"
                                        alt="Delete User Permanently"
                                        className="cursor-pointer cancel admin-user-action-icon"
                                        onClick={() => {
                                            setDeleteModal('deleteUserPermanent');
                                            setSelectedUser(row?.original);
                                        }}
                                        role="button"
                                    />
                                </CustomTooltip>
                            </div>
                    ) :
                            <>
                                {
                                    !showCheckbox &&
                                    <CustomTooltip title="Edit User">
                                        <img
                                            src="/icons/edit.svg"
                                            alt="Edit User"
                                            className="me-2 cursor-pointer manage-edit admin-user-action-icon"
                                            onClick={() => {
                                                setData(row?.original)
                                            }}
                                            role="button"
                                        />
                                    </CustomTooltip>
                                }
                                {
                                    <CustomTooltip title="Manage Domains">
                                        <img
                                            src="/icons/globe.svg"
                                            alt="Manage Domains"
                                            className="me-2 cursor-pointer admin-user-action-icon"
                                            onClick={() => {
                                                setSelectedUser(row?.original);
                                                setOpenUserDomains(true);
                                            }}
                                            role="button"
                                        />
                                    </CustomTooltip>
                                }
                                {
                                    userInfoData?.id == row?.original?.id && userInfoData?.email == row?.original?.email ?
                                        <></> :
                                        <>
                                            {canImpersonateRow(row?.original) &&
                                                <CustomTooltip title="Impersonate User">
                                                    <img
                                                        src="/icons/user-group.svg"
                                                        alt="Impersonate User"
                                                        className="me-2 cursor-pointer admin-user-action-icon"
                                                        onClick={() => requestImpersonation(row?.original)}
                                                        role="button"
                                                    />
                                                </CustomTooltip>
                                            }
                                            <CustomTooltip title={row?.original?.company_id ? "Ban User" : "Delete User"}>
                                                <img
                                                    src="/icons/delete.svg"
                                                    alt={row?.original?.company_id ? "Ban User" : "Delete User"}
                                                    className="cursor-pointer cancel admin-user-action-icon"
                                                    onClick={() => {
                                                        const hasCompany = !!row?.original?.company_id;
                                                        const hasDocs = row?.original?.docs_count > 0;
                                                        setDeleteModal(
                                                            hasCompany && hasDocs ? 'move'
                                                            : hasCompany ? 'delete'
                                                            : hasDocs ? 'warnDelete'
                                                            : 'deleteUserPermanent'
                                                        );
                                                        setSelectedUser(row?.original);
                                                    }}
                                                    role="button"
                                                />
                                            </CustomTooltip>
                                        </>
                                }
                                {
                                    row?.original?.is_online ?
                                        <FontAwesomeIcon icon={faCircle} className="ms-2 user-active" /> :
                                        <></>
                                }
                            </>}
                    </>
                )
            },
        ];
    }, [showCheckbox, type, userInfoData, userIds, canRequest]);

    return (
        <>
            <DeleteOrgModal
                title={"Ban User"}
                openModal={deleteModal == 'delete'}
                closeModal={() => { setDeleteModal(null) }}
                name={`ban ${selectedUser?.name || selectedUser?.email}`}
                handleDelete={handleDelete}
            />
            <DeleteOrgModal
                title={"Delete User"}
                openModal={deleteModal === "deleteUserPermanent"}
                closeModal={() => { setDeleteModal(null) }}
                name={`delete ${selectedUser?.name || selectedUser?.email}`}
                handleDelete={handlePermanentDelete}
            />
            <DeleteOrgModal
                title={"Delete User"}
                openModal={selectAction === "delete" && userIds.length > 0}
                closeModal={handleCloseModal}
                name={'delete all selected users'}
                handleDelete={handlePerformAction}
            />
            <DeleteOrgModal
                title={"Send Mail"}
                openModal={selectAction === "sendMail" && userIds.length > 0}
                closeModal={handleCloseModal}
                name={'send mail to all selected users'}
                handleDelete={handlePerformAction}
            />
            <DeleteOrgModal
                title={"Delete User"}
                openModal={deleteModal === 'warnDelete'}
                closeModal={() => { setDeleteModal(null) }}
                name={`delete ${selectedUser?.name || selectedUser?.email}`}
                warning={`This user has ${selectedUser?.docs_count ?? 0} KDF(s)${(selectedUser?.tracking_links_count ?? 0) > 0 ? ` and ${selectedUser?.tracking_links_count} LaserLink(s)` : ''}. Their account and data will be deleted permanently.`}
                handleDelete={handleMoveModalDeleteAll}
            />
            <MoveDataModal
                openModal={deleteModal == 'move'}
                closeModal={() => { setDeleteModal(null) }}
                userData={selectedUser}
                handleSubmit={() => setDeleteModal('moveDataModal')}
            />
            <CompanyUsersModal
                openModal={deleteModal == 'moveDataModal'}
                closeModal={() => setDeleteModal(null)}
                companyId={companyId || selectedUser?.company_id}
                selectedUserId={selectedUser?.id}
                onSuccess={() => { setDeleteModal(null); setRefetch(true); }}
            />

            <div className="top-parent admin-user-table-wrapper">
                <CustomTable
                    data={currentPageData}
                    columnData={columns}
                    showFilters={showCheckbox}
                    manualSort
                    sortState={sortState || undefined}
                    onSortChange={handleSortChange}
                />
            </div>
            {totalData > dataPerPage && (
                <AdminPagination
                    dataPerPage={dataPerPage}
                    totalData={totalData}
                    onPageChange={handlePageChange}
                    currentPage={currentPage}
                    showRange={true}
                    previousLabel="‹ Previous"
                    nextLabel="Next ›"
                />
            )}
            <UserDomainsModal
                openModal={openUserDomains}
                closeModal={() => setOpenUserDomains(false)}
                user={selectedUser}
            />
            <BulkUserDomainsModal
                openModal={openUserBulkDomains}
                closeModal={() => {
                    setOpenUserBulkDomains(false);
                    setSelectAction('action');
                }}
                userIds={userIds}
            />
        </>
    )
}
export default UsersListing
